import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {ASHFALL_KIND} from '../../../configs/biomeEffects/ashfall';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  clamp01,
  lerp,
  mixTints,
  pickPointInBox,
  randomIn,
  randomSign,
  resolveLifeEnvelope,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ПЕПЕЛ С ОСТЫВАЮЩИМИ УГОЛЬКАМИ (пепельная подложка). Чистые функции без Phaser
 * и DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Группа — горсть хлопьев, родившихся в одну секунду там, куда смотрит камера.
 * Хлопок проступает, плывет по ветру матча, понемногу опускаясь вниз по экрану
 * и покачиваясь, кувыркается (то плашмя, то ребром) и тает. Середина его пути —
 * случайная точка области рождения: хлопья, которых сносит ветром, не
 * скапливаются у подветренного края кадра. Срок у хлопьев одной горсти почти
 * общий: спрайты горсти заняты, пока жив ее последний хлопок.
 *
 * Хлопья трех видов: светлый пепел, угольно-темный и тлеющий уголек. Уголек —
 * две частицы: сам хлопок, остывающий от оранжевого до темного, и светящийся
 * ореол над ним, который мерцает и гаснет вместе с жаром.
 *
 * Горстей и хлопьев в них столько, чтобы в воздухе держалась заданная плотность
 * на ячейку, но не больше потолка: чем дальше камера, тем пепел реже.
 */

const PALE = 'pale';
const CHARCOAL = 'charcoal';
const CINDER = 'cinder';
const CINDER_GLOW = 'cinderGlow';

const createTracks = (context, params) => {
  const {hexSize, wind} = context;
  const {flake, pale, charcoal, cinder} = params;
  const across = {x: -wind.y, y: wind.x};
  // Площадь шестиугольной ячейки
  const cellArea = 1.5 * Math.sqrt(3) * hexSize * hexSize;
  // В воздухе держится (хлопьев в горсти) x (срок хлопка) / (пауза между горстями)
  const groupsPerLife = ((flake.lifeMs.min + flake.lifeMs.max) * (1 - flake.lifeSpread / 2))
    / (params.intervalMs.min + params.intervalMs.max);

  const pickKind = (rng) => {
    const roll = rng() * (pale.share + charcoal.share + cinder.share);
    if (roll < cinder.share) {
      return CINDER;
    }
    return roll < cinder.share + charcoal.share ? CHARCOAL : PALE;
  };

  const buildGroup = ({rng, view}) => {
    const box = resolveSpawnBox(context, view, {
      fieldMarginFactor: params.fieldMarginFactor, viewMarginFactor: params.viewMarginFactor,
    });
    const cellCount = ((box.right - box.left) * (box.bottom - box.top)) / cellArea;
    const liveFlakes = Math.min(params.maxFlakes, params.densityPerCell * cellCount);
    // Дробное число хлопьев горсти округляется случайно: в среднем выходит ровно столько
    const count = Math.floor(liveFlakes / groupsPerLife + rng());
    if (count < 1) {
      return null;
    }

    const groupLifeMs = randomIn(rng, flake.lifeMs);
    const particles = [];
    let durationMs = 0;
    for (let index = 0; index < count; index += 1) {
      const kind = pickKind(rng);
      const lifeMs = groupLifeMs * (1 - rng() * flake.lifeSpread);
      const windSpeed = (hexSize * randomIn(rng, flake.windSpeedFactor)) / 1000;
      const fallSpeed = (hexSize * randomIn(rng, flake.fallSpeedFactor)) / 1000;
      const velocityX = wind.x * windSpeed;
      const velocityY = wind.y * windSpeed + fallSpeed;
      // Случайная точка области — середина пути хлопка, а не его начало
      const middle = pickPointInBox(rng, box);
      const body = {
        kind,
        layer: FIELD_BIOME_LAYER_KEYS.AIR,
        texture: FIELD_BIOME_TEXTURES.FLAKE,
        delayMs: rng() * flake.staggerMs,
        lifeMs,
        startX: middle.x - (velocityX * lifeMs) / 2,
        startY: middle.y - (velocityY * lifeMs) / 2,
        velocityX,
        velocityY,
        size: hexSize * randomIn(rng, flake.sizeFactor),
        sway: hexSize * randomIn(rng, flake.swayFactor),
        swayPeriodMs: randomIn(rng, flake.swayPeriodMs),
        swayPhase: rng() * TWO_PI,
        spinRadPerMs: (toRadians(randomIn(rng, flake.spinDegPerSecond)) / 1000) * randomSign(rng),
        spinPhase: rng() * TWO_PI,
        tumblePeriodMs: randomIn(rng, flake.tumblePeriodMs),
        tumblePhase: rng() * TWO_PI,
      };
      durationMs = Math.max(durationMs, body.delayMs + lifeMs);

      if (kind !== CINDER) {
        const look = kind === CHARCOAL ? charcoal : pale;
        particles.push({...body, tint: look.tint, alpha: randomIn(rng, look.alpha)});
      } else {
        const ember = {
          ...body,
          coolShare: randomIn(rng, cinder.coolShare),
          flickerHz: randomIn(rng, cinder.flickerHz),
          flickerPhase: rng() * TWO_PI,
        };
        particles.push({...ember, tint: cinder.hotTint});
        // Ореол — светящийся слой прямо над хлопьями: рисуется поверх уголька
        particles.push({
          ...ember,
          kind: CINDER_GLOW,
          layer: FIELD_BIOME_LAYER_KEYS.AIR_GLOW,
          texture: FIELD_BIOME_TEXTURES.DOT,
          tint: cinder.glowTint,
        });
      }
    }
    return {durationMs, particles};
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const ageMs = elapsedMs - particle.delayMs;
    const life = ageMs / particle.lifeMs;
    if (!(life > 0) || !(life < 1)) {
      return writeHiddenPose(out);
    }
    const fadeShare = Math.min(0.45, flake.fadeMs / particle.lifeMs);
    const envelope = resolveLifeEnvelope(life, fadeShare, fadeShare);
    const sway = particle.sway * Math.sin((TWO_PI * ageMs) / particle.swayPeriodMs + particle.swayPhase);
    const x = particle.startX + particle.velocityX * ageMs + across.x * sway;
    const y = particle.startY + particle.velocityY * ageMs + across.y * sway;
    // Жар уголька: 1 при рождении, 0 — остыл
    const heat = particle.coolShare ? 1 - smoothstep(0, particle.coolShare, life) : 0;

    if (particle.kind === CINDER_GLOW) {
      if (!(heat > 0)) {
        return writeHiddenPose(out);
      }
      const flicker = 1 + cinder.flickerDepth
        * Math.sin((TWO_PI * particle.flickerHz * ageMs) / 1000 + particle.flickerPhase);
      const size = particle.size * cinder.glowScale;
      return writePose(out, x, y, clamp01(cinder.glowAlpha * heat * flicker * envelope), size, size, 0);
    }

    // Кувырок: хлопок поворачивается ребром и снова плашмя
    const flat = Math.abs(Math.cos((TWO_PI * ageMs) / particle.tumblePeriodMs + particle.tumblePhase));
    const width = particle.size * lerp(flake.tumbleEdgeShare, 1, flat);
    const rotation = particle.spinPhase + particle.spinRadPerMs * ageMs;
    if (particle.kind !== CINDER) {
      return writePose(out, x, y, particle.alpha * envelope, width, particle.size, rotation);
    }
    writePose(out, x, y, lerp(cinder.coolAlpha, cinder.hotAlpha, heat) * envelope, width, particle.size, rotation);
    out.tint = mixTints(cinder.coolTint, cinder.hotTint, heat);
    return out;
  };

  return [{
    schedule: {
      firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups,
      prewarm: params.prewarm,
    },
    buildGroup,
    resolvePose,
  }];
};

const ashfall = {
  kind: ASHFALL_KIND,
  textures: [FIELD_BIOME_TEXTURES.FLAKE, FIELD_BIOME_TEXTURES.DOT],
  createTracks,
};

export default ashfall;
